/**
 * editor2/aiEmail.ts — AIメール生成のコアロジック（Track C, 2026-07-04）
 *
 * usePaymentEmail から呼ばれる非React モジュール。3つの改良を実装:
 *   C1: 出力スキーマに reasoning / assumptions / checkpoints を追加
 *       （「なぜこの内容にしたか」をスタッフに提示する）
 *   C2: マルチターン会話（contents を蓄積し、改善指示が文脈を保って効く）
 *   C3: 読み取り専用 tool（在庫検索・料金計算等）を許可し、
 *       「この条件なら受け入れ可」等の判断を実データに接地させる
 *
 * 安全設計:
 *   - tool は EMAIL_TOOL_NAMES の読み取り専用ホワイトリストのみ。
 *     宣言もディスパッチも二重にガードし、create/update 系は構造的に呼べない
 */
import { postAgentChat } from '../../agent/api';
import { AGENT_TOOL_DECLARATIONS, dispatchTool } from '../../agent/tools';
import type { GeminiContent } from '../../agent/types';
import { getEmailBaseRules } from './emailRules';
import { recordAgentExecution, toResultStatus, summarizeToolResult } from '../../agent/executionLog';

/** メール生成に許可する読み取り専用 tool（書き込み系は絶対に含めない） */
export const EMAIL_TOOL_NAMES = [
  'search_vehicle_types',
  'list_all_vehicle_types',
  'calculate_price',
  'search_reservations',
] as const;

const EMAIL_TOOL_SET = new Set<string>(EMAIL_TOOL_NAMES);

/** ホワイトリストの宣言のみ Gemini に渡す */
export const EMAIL_TOOL_DECLARATIONS = AGENT_TOOL_DECLARATIONS.filter(
  (d: any) => EMAIL_TOOL_SET.has(d.name),
);

/** AI の判断メタ情報（C1） */
export interface AiEmailMeta {
  /** なぜこの内容・トーンにしたか（tool で確認した事実も含む） */
  reasoning: string;
  /** AI が置いた前提（データに無く仮定した事項） */
  assumptions: string[];
  /** 送信前にスタッフが確認すべき点 */
  checkpoints: string[];
  /** 実行された tool（例: 'calculate_price'） */
  toolLog: string[];
}

export interface AiEmailOutput {
  subject: string;
  body: string;
  /** メールを変更せずスタッフに返す返答（質問・確認・説明）。対話モード（2026-07-09） */
  reply: string;
  meta: AiEmailMeta;
}

/** 対話スレッドの1エントリ（モーダルのチャットUIに表示） */
export interface AiChatEntry {
  id: string;
  role: 'user' | 'ai';
  text: string;
  /** ai エントリの種別: draft-update=本文を更新した / reply=返答のみ */
  kind?: 'draft-update' | 'reply';
  /** draft-update 時の対応バージョン */
  versionId?: string;
  assumptions?: string[];
  checkpoints?: string[];
  toolLog?: string[];
}

/** 生成/改善の1バージョン（C2 履歴） */
export interface AiEmailVersion {
  id: string;
  createdAt: string;
  /** このバージョンを生んだ指示（初回は生成指示、以降は改善指示） */
  instruction: string;
  subject: string;
  body: string;
  meta: AiEmailMeta | null;
}

const MAX_TOOL_LOOP = 4;

/** Gemini の出力からコードフェンスを剥がす */
export function stripCodeFence(text: string): string {
  return text
    .replace(/^\s*```(?:json)?\s*/i, '')
    .replace(/\s*```\s*$/i, '')
    .trim();
}

/** 出力 JSON をパース。失敗時は全文を body として扱う（従来互換） */
export function parseAiEmailOutput(raw: string, toolLog: string[]): AiEmailOutput {
  const cleaned = stripCodeFence(raw);
  let subject = '';
  let body = '';
  let reply = '';
  let reasoning = '';
  let assumptions: string[] = [];
  let checkpoints: string[] = [];
  try {
    const parsed = JSON.parse(cleaned);
    subject = String(parsed.subject || '').trim();
    body = String(parsed.body || '').trim();
    reply = String(parsed.reply || '').trim();
    reasoning = String(parsed.reasoning || '').trim();
    if (Array.isArray(parsed.assumptions)) {
      assumptions = parsed.assumptions.map((a: any) => String(a)).filter(Boolean);
    }
    if (Array.isArray(parsed.checkpoints)) {
      checkpoints = parsed.checkpoints.map((c: any) => String(c)).filter(Boolean);
    }
  } catch {
    body = cleaned;
  }
  return {
    subject,
    body,
    reply,
    meta: { reasoning, assumptions, checkpoints, toolLog: [...toolLog] },
  };
}

/**
 * システムプロンプト（生成・改善で共通。C2 で両者を同一の会話に統一）
 */
export function buildAiEmailSystemInstruction(): string {
  return [
    getEmailBaseRules(),
    '\n---\n',
    'あなたはレンタカー店舗のスタッフがお客様に送るメールを作成・改善するアシスタントです。',
    '上記の「メール作成基本ルール」を必ず守ってください。',
    '',
    '## ツールの使用（重要）',
    '- 在庫の有無・空車状況・料金に言及するメールを書く場合は、推測で書かず、',
    '  必ずツール（search_vehicle_types / list_all_vehicle_types / calculate_price）で確認してから書く',
    '- 「この条件なら受け入れ可能」等の判断は、ツールで確認した事実を根拠にする',
    '- 確認した事実（例: ◯日に△△が2台空車、料金¥◯◯）は reasoning に必ず記載する',
    '- ツールで確認できなかった/確認不要な場合は、その旨を assumptions に書く',
    '- 料金の内訳はツール結果の price_lines のラベルどおりに書く（深夜早朝手数料を「手数料」「税」等に言い換えない）。daily_list_price は1日目の定価で、期間の基本料金ではない',
    '',
    '## 出力形式（厳守）',
    '最終回答は次のどちらか一方の JSON だけを返す。前置き・補足説明・コードブロック（```）を一切付けない。',
    '',
    '(A) メールを作成・更新する場合:',
    '{',
    '  "subject": "メール件名（言語に合わせる。変更不要なら現行のまま）",',
    '  "body": "メール本文",',
    '  "reasoning": "なぜこの内容・トーン・判断にしたか。ツールで確認した事実があれば具体的に（スタッフ向け・日本語）",',
    '  "assumptions": ["AIが置いた前提や仮定（無ければ空配列）"],',
    '  "checkpoints": ["送信前にスタッフが確認すべき点（無ければ空配列）"]',
    '}',
    '',
    '(B) メールを変更せずスタッフと対話する場合（指示が曖昧・情報が不足していて確認したい、',
    '    質問に答えるだけ、提案を先に相談したい等）:',
    '{"reply": "スタッフへの返答・質問（日本語）"}',
    '- 勝手な想像で書き換えるより、迷ったら (B) で確認する方が望ましい',
    '- (B) では subject / body を含めない（現在のメールが維持される）',
    '',
    '- reasoning / assumptions / checkpoints / reply は宛先のお客様には送られない。スタッフ向けに必ず日本語で書く',
    '',
    '## body の構造',
    '- 冒頭: ヘッダー（顧客名 + 予約コード行 + 空行）',
    '- 中央: 本文',
    '- 末尾: フッター（区切り線 -------------------- + 店舗情報ブロック + 区切り線）',
    '- ヘッダーの言語別ラベル・フッターは「メール作成基本ルール」の通り',
    '- 改善指示の場合もヘッダー・フッター・予約コード等の固有値は維持する',
    '',
    '## 言語',
    '- 指示に「英文で」「韓国語で」等の指定があればその言語で（reasoning 等は常に日本語）',
    '- 指定がなければ現在の本文と同じ言語、新規なら日本語',
    '',
    '## データの扱い',
    '- 「予約情報」やツール結果に無い金額・日時・予約コードを推測で書かない',
    '- 不明な値はプレースホルダ（例: [計算した料金]）にし、checkpoints にその旨を書く',
  ].join('\n');
}

export interface AiEmailTurnResult {
  output: AiEmailOutput;
  /** この turn で会話履歴に追加すべき contents（model 応答 + functionResponse 含む） */
  appendedContents: GeminiContent[];
}

/** functionCall パートの抽出 */
function extractFunctionCalls(content: GeminiContent | undefined): Array<{ name: string; args: Record<string, any> }> {
  if (!content?.parts) return [];
  return content.parts
    .filter((p: any) => p.functionCall && p.functionCall.name)
    .map((p: any) => p.functionCall);
}

function extractText(content: GeminiContent | undefined): string {
  return content?.parts?.find((p: any) => typeof p.text === 'string' && p.text.trim())?.text || '';
}

/**
 * 1ユーザー指示ぶんの生成 turn を実行する（tool ループ込み・C3）。
 * @param priorContents これまでの会話（呼び出し側が保持）。ユーザーの新しい指示を末尾に含めて渡す
 * @returns 出力と、会話履歴に追記すべき contents（次 turn に引き継ぐ）
 */
export async function runAiEmailTurn(priorContents: GeminiContent[]): Promise<AiEmailTurnResult> {
  const systemInstruction = buildAiEmailSystemInstruction();
  const contents: GeminiContent[] = [...priorContents];
  const appended: GeminiContent[] = [];
  const toolLog: string[] = [];

  for (let i = 0; i < MAX_TOOL_LOOP; i++) {
    const result = await postAgentChat({
      system_instruction: systemInstruction,
      contents: [...contents], // 後続の push が呼び出し済みリクエストに影響しないようコピー
      tools: [{ functionDeclarations: EMAIL_TOOL_DECLARATIONS }],
      rule_scope: 'email', // DB の追加ルールをサーバー側で注入（Track A2）
    });
    if (!result.success || !result.content) {
      throw new Error(result.error || 'AIから空のレスポンスが返りました');
    }
    const modelContent: GeminiContent = { role: 'model', parts: result.content.parts || [] };
    const calls = extractFunctionCalls(modelContent);

    if (calls.length === 0) {
      const text = extractText(modelContent);
      if (!text) throw new Error('AIから空のレスポンスが返りました');
      contents.push(modelContent);
      appended.push(modelContent);
      return { output: parseAiEmailOutput(text, toolLog), appendedContents: appended };
    }

    // tool 実行（読み取り専用ホワイトリストの二重ガード）
    contents.push(modelContent);
    appended.push(modelContent);
    const responseParts: any[] = [];
    for (const call of calls) {
      if (!EMAIL_TOOL_SET.has(call.name)) {
        responseParts.push({
          functionResponse: {
            name: call.name,
            response: { error: `メール生成では ${call.name} は使用できません（読み取り専用ツールのみ）` },
          },
        });
        continue;
      }
      const startedAt = Date.now();
      try {
        toolLog.push(call.name);
        const result = await dispatchTool(call.name, call.args || {}, {
          // 読み取り専用 tool は confirm/wizard を使わないが、万一の呼び出しは常に拒否
          requestConfirm: async () => false,
        });
        recordAgentExecution({
          source: 'email',
          tool_name: call.name,
          args: call.args || {},
          result_status: toResultStatus(result),
          result_summary: summarizeToolResult(result),
          duration_ms: Date.now() - startedAt,
        });
        responseParts.push({ functionResponse: { name: call.name, response: { result } } });
      } catch (e: any) {
        recordAgentExecution({
          source: 'email',
          tool_name: call.name,
          args: call.args || {},
          result_status: 'error',
          result_summary: e?.message || String(e),
          duration_ms: Date.now() - startedAt,
        });
        responseParts.push({
          functionResponse: { name: call.name, response: { error: e?.message || String(e) } },
        });
      }
    }
    const responseContent: GeminiContent = { role: 'user', parts: responseParts };
    contents.push(responseContent);
    appended.push(responseContent);
  }

  throw new Error(`AI処理が長引いたため中断しました（ツール実行 最大${MAX_TOOL_LOOP}回）`);
}
